import React from 'react';
import { Button } from '@avila-tek/ui/src';
import { validateNumber } from 'avilatek-utils';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { BenefitRequest, Company } from '../../../models';
import { useNotify, useUser } from '../../../hooks';
import {
  MARK_BENEFIT_REQUEST_PAID,
  UPDATE_BENEFIT_REQUEST,
} from '../../../graphql/mutation';
import { roles } from '../../../static';

interface RequestFormButtonsProps {
  formState: Partial<BenefitRequest>;
  updateRequest: (state: Partial<BenefitRequest>) => Promise<void>;
  request: BenefitRequest;
}

function RequestFormButtons({
  updateRequest,
  formState,
  request,
}: RequestFormButtonsProps) {
  const notify = useNotify();
  const router = useRouter();
  const [user] = useUser();
  const [disable, setDisable] = React.useState(false);

  const [updateRequestMutation] = useMutation<{
    updateBenefitRequest: {
      recordId: string;
    };
  }>(UPDATE_BENEFIT_REQUEST);

  const [markBenefitRequestPaidMutation] = useMutation<{
    markBenefitRequestPaid: BenefitRequest;
  }>(MARK_BENEFIT_REQUEST_PAID);

  const handleMarkAsPaid = async () => {
    try {
      if (disable) return;
      setDisable(true);
      const { data } = await markBenefitRequestPaidMutation({
        variables: {
          data: {
            adminId: user?._id,
            benefitRequestsId: [request._id],
            companyId: (request.company as Company)._id,
          },
        },
      });
      if (data?.markBenefitRequestPaid) {
        notify('Solicitud marcada como pagada', 'success');
        const path =
          user?.userType === roles.admin ? '/app/receivable' : '/app/requests';

        router.push(path);
      } else {
        notify('No se pudo marcar la solicitud como pagada', 'error');
      }
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setDisable(false);
    }
  };

  const inactivateRequest = async () => {
    try {
      if (disable) return;
      setDisable(true);
      const { data } = await updateRequestMutation({
        variables: {
          record: {
            active: false,
            status: 'cancelled',
          },
          filter: {
            _id: request._id,
          },
        },
      });
      if (data?.updateBenefitRequest?.recordId) {
        notify('Solicitud inactivada con éxito', 'success');
        router.push(`/app/receivable/${request.company._id}`);
      } else {
        notify('No se pudo inactivar la solicitud', 'error');
      }
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setDisable(false);
    }
  };

  const handleAccept = async () => {
    if (!validateApprovedAmount()) return;

    await updateRequest({
      ...formState,
      amountApproved: formState.amountApproved,
    });
  };

  const validateApprovedAmount = () => {
    if (
      !validateNumber(formState.amountApproved) ||
      formState.amountApproved < 0 ||
      formState.amountApproved > request.amountRequested
    ) {
      notify(
        'El aprobado debe ser mayor que  y menor que el monto solicitado',
        'error'
      );
      return false;
    }
    return true;
  };

  const handleReject = async () => {
    await updateRequest({
      ...formState,
      amountApproved: 0,
    });
  };

  return (
    <div className="flex w-full  justify-end ">
      <div className="w-full md:w-1/2 flex space-x-5 ">
        {request.status === 'pending' ? (
          <Button
            onClick={(e) => {
              e.preventDefault();
              handleAccept();
            }}
          >
            Aceptar solicitud
          </Button>
        ) : null}
        {request.status === 'pending' ? (
          <Button
            onClick={(e) => {
              e.preventDefault();
              handleReject();
            }}
            className="border-red-500 text-red-500 bg-white border"
          >
            Rechazar solicitud
          </Button>
        ) : null}
        {request.status === 'approved' ? (
          <Button
            onClick={(e) => {
              e.preventDefault();
              inactivateRequest();
            }}
            className="border-red-500 text-red-500 bg-white border"
          >
            Desactivar solicitud
          </Button>
        ) : null}
        {user.userType === roles?.admin &&
        request.active &&
        request.status === 'approved' ? (
          <Button
            onClick={(e) => {
              e.preventDefault();
              handleMarkAsPaid();
            }}
            className="border-purple-300 text-purple-300 bg-white border"
          >
            Marcar como pagado
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default RequestFormButtons;
